import {
    BarChartLine,
    Bell,
    Boxes,
    CalendarCheck,
    ChatLeftText,
    CheckCircle,
    Code,
    CurrencyExchange,
    Diagram2,
    FileEarmarkBarGraph,
    Key,
    Layers,
    Lightning,
    ListUl,
    Lock,
    PersonGear,
    Phone,
    Search,
    ShieldLock,
    Tags,
    Translate,
} from "react-bootstrap-icons";
import {APP_ICONS, FallbackIcon, type IconComp} from "~/components/_appIcons";
import type {AppItem} from "~/components/_appsData";
import {appCategories} from "~/components/_appsData";
import {capabilities, industries} from "~/components/_data";

/* ============================================================================
 * AllAppsGrid, structural homage to ClickUp's homepage "All apps, AI Agents,
 * and humans in ClickUp" tile-grid section, restyled with GJ tokens.
 *
 * 50 uniform tiles in a 5×10 layout (10 cols × 5 rows on lg+, 5 cols × 10 rows
 * on mobile). The grid fills the viewport on lg+ (section min-h-screen, grid
 * flex-1 below the heading) so it reads as a full-screen "everything we
 * ship" canvas. Hairlines between cells come from a 1px gap over a tinted
 * section background.
 *
 * Content: 24 apps (from _appsData) + 26 platform capabilities to fill out
 * the 50-cell grid. All tiles are visually equal, no hero highlights.
 * ============================================================================ */

type TileData = {name: string; Icon: IconComp};

/* The 9 always-blue apps arranged in a 3×3 block with AI Agent at the dead
 * center. Order: center, then 4 edge-adjacent (top, right, bottom, left),
 * then 4 diagonals (TL, TR, BL, BR). Both grid dimensions are odd so the
 * 3×3 sits on the true center of the larger 9×5 grid. */
const HIGHLIGHTED_SLUGS = [
    "ai-agent", // center
    "analytics", // top
    "crm", // right
    "sales", // bottom
    "campaigns", // left
    "attendance", // top-left (diagonal), represents People & Operations
    "online-store", // top-right (diagonal)
    "inventory", // bottom-left (diagonal)
    "website", // bottom-right (diagonal)
] as const;
const HIGHLIGHTED_SLUG_SET: ReadonlySet<string> = new Set(HIGHLIGHTED_SLUGS);

/* Position classes per highlighted tile.
 * Mobile grid is 5 cols × 9 rows → center cell is col 3, row 5.
 * Desktop grid is 9 cols × 5 rows → center cell is col 5, row 3. */
const highlightedPositions: ReadonlyArray<string> = [
    "col-start-3 row-start-5 lg:col-start-5 lg:row-start-3", // AI Agent, center
    "col-start-3 row-start-4 lg:col-start-5 lg:row-start-2", // Analytics, top
    "col-start-4 row-start-5 lg:col-start-6 lg:row-start-3", // CRM, right
    "col-start-3 row-start-6 lg:col-start-5 lg:row-start-4", // Sales, bottom
    "col-start-2 row-start-5 lg:col-start-4 lg:row-start-3", // Campaigns, left
    "col-start-2 row-start-4 lg:col-start-4 lg:row-start-2", // Connector, top-left
    "col-start-4 row-start-4 lg:col-start-6 lg:row-start-2", // Online Store, top-right
    "col-start-2 row-start-6 lg:col-start-4 lg:row-start-4", // Inventory, bottom-left
    "col-start-4 row-start-6 lg:col-start-6 lg:row-start-4", // Website, bottom-right
];

const allAppsFlat: Array<AppItem> = appCategories.flatMap((cat) => cat.apps);

const highlightedTiles: Array<TileData> = HIGHLIGHTED_SLUGS.map((slug) => allAppsFlat.find((a) => a.slug === slug))
    .filter((a): a is AppItem => a != null)
    .map((app) => ({name: app.name, Icon: APP_ICONS[app.slug] ?? FallbackIcon}));

const appTiles: Array<TileData> = allAppsFlat
    .filter((a) => !HIGHLIGHTED_SLUG_SET.has(a.slug))
    .map((app) => ({name: app.name, Icon: APP_ICONS[app.slug] ?? FallbackIcon}));

const capabilityTiles: Array<TileData> = [
    {name: "Dashboards", Icon: BarChartLine},
    {name: "Reports", Icon: FileEarmarkBarGraph},
    {name: "Workflows", Icon: Diagram2},
    {name: "Automations", Icon: Lightning},
    {name: "Integrations", Icon: Boxes},
    {name: "Notifications", Icon: Bell},
    {name: "Templates", Icon: Layers},
    {name: "Mobile App", Icon: Phone},
    {name: "API Access", Icon: Code},
    {name: "SSO", Icon: Key},
    {name: "Audit Logs", Icon: ShieldLock},
    {name: "Custom Fields", Icon: ListUl},
    {name: "Permissions", Icon: PersonGear},
    {name: "Bulk Actions", Icon: CheckCircle},
    {name: "Search", Icon: Search},
    {name: "Multi-currency", Icon: CurrencyExchange},
    {name: "Multi-language", Icon: Translate},
    {name: "Comments", Icon: ChatLeftText},
    {name: "Tags", Icon: Tags},
    {name: "Calendar Sync", Icon: CalendarCheck},
    {name: "Two-factor Auth", Icon: Lock},
];

const allTiles: Array<TileData> = [...appTiles, ...capabilityTiles];

/* Deterministic-but-scattered timing per tile. Pseudo-random hash of the
 * tile's index gives a different duration (1.6–3.2s) per tile and a very
 * small (0–60ms) delay, effectively instant kickoff with imperceptible
 * micro-variation. The randomness of the visual is carried by the duration
 * differences, which desync the tiles within the first cycle. */
function popTiming(idx: number, salt: number): {duration: number; delay: number} {
    const seed = ((idx + salt) * 2654435761) >>> 0;
    return {
        duration: 1600 + (seed % 1600),
        delay: seed % 60,
    };
}

function Tile({data, idx}: {data: TileData; idx: number}) {
    const {Icon} = data;
    const {duration, delay} = popTiming(idx, 41);
    return (
        <div
            className="aspect-square lg:aspect-auto bg-surface-primary-background grid place-items-center gap-1 lg:gap-2 p-2 lg:p-4 text-text-primary relative transition-all duration-700 hover:bg-surface-surface-card hover:text-primary-500 hover:shadow-[0_12px_32px_-8px_rgba(31,64,203,0.30)] hover:z-10 hover:scale-105 group-has-[.is-center:hover]/appsgrid:[animation:tile-pop_var(--pop-dur)_ease-in-out_var(--pop-delay)_infinite]"
            style={{"--pop-dur": `${duration}ms`, "--pop-delay": `${delay}ms`} as React.CSSProperties}
        >
            <Icon className="w-5 h-5 lg:w-7 lg:h-7" />
            <span className="text-[0.625rem]/[0.875rem] lg:text-nav-text-b text-center">{data.name}</span>
        </div>
    );
}

/* Bouncy easing for the pop, cubic-bezier with y2 > 1 overshoots target
 * before settling, producing a noticeable spring. */
const POP_EASE = "ease-[cubic-bezier(0.34,1.56,0.64,1)]";

function HighlightedTile({data, position, isCenter, idx}: {data: TileData; position: string; isCenter?: boolean; idx: number}) {
    const {Icon} = data;

    if (isCenter === true) {
        /* AI Agent, always rendered in the "popped" state (white bg, shadow,
         * scale-105, z-10). Continuously animates `ai-agent-shake` on a 2s
         * loop. Marked as `is-center` so hovering it (or any other center)
         * triggers the grid-wide pop via the group/appsgrid mechanism. The
         * `group-has-[.is-center:hover]/appsgrid:[animation:none]` utility
         * pauses the shake whenever ANY center tile is hovered. */
        return (
            <div
                className={`is-center aspect-square lg:aspect-auto bg-surface-surface-card grid place-items-center gap-1 lg:gap-2 p-2 lg:p-4 text-primary-500 relative scale-105 z-10 shadow-[0_12px_32px_-8px_rgba(31,64,203,0.30)] transition-all duration-700 ${POP_EASE} animate-[ai-agent-shake_2s_ease-in-out_infinite] hover:scale-110 hover:shadow-[0_16px_40px_-8px_rgba(31,64,203,0.45)] group-has-[.is-center:hover]/appsgrid:[animation:none] ${position}`}
            >
                <Icon className="w-5 h-5 lg:w-7 lg:h-7" />
                <span className="text-[0.625rem]/[0.875rem] lg:text-nav-text-b text-center">{data.name}</span>
            </div>
        );
    }

    /* The 8 surrounding blue tiles, `is-center` (hover triggers the grid
     * pop) + the same group-has pop utility so they themselves participate
     * in the cascade when a sibling center tile is hovered. Text stays blue. */
    const {duration, delay} = popTiming(idx, 17);
    return (
        <div
            className={`is-center aspect-square lg:aspect-auto bg-surface-primary-background grid place-items-center gap-1 lg:gap-2 p-2 lg:p-4 text-primary-500 relative transition-all duration-700 ${POP_EASE} hover:bg-surface-surface-card hover:shadow-[0_12px_32px_-8px_rgba(31,64,203,0.30)] hover:scale-105 hover:z-10 group-has-[.is-center:hover]/appsgrid:[animation:tile-pop_var(--pop-dur)_ease-in-out_var(--pop-delay)_infinite] ${position}`}
            style={{"--pop-dur": `${duration}ms`, "--pop-delay": `${delay}ms`} as React.CSSProperties}
        >
            <Icon className="w-5 h-5 lg:w-7 lg:h-7" />
            <span className="text-[0.625rem]/[0.875rem] lg:text-nav-text-b text-center">{data.name}</span>
        </div>
    );
}

export function AllAppsGrid() {
    return (
        <section
            aria-labelledby="all-apps-title"
            className="grid grid-cols-1"
        >
            <header className="gj-px-screen-edge-constrained gj-py-section-divider text-left lg:text-center">
                <div className="max-w-3xl mx-auto grid gap-md">
                    <h2
                        id="all-apps-title"
                        className="text-heading2-25 text-text-primary"
                    >
                        All <span className="text-gradient-blue">apps</span>, <span className="text-gradient-blue">industries</span>, and{" "}
                        <span className="text-gradient-blue">capabilities</span> in Intellsys OS.
                    </h2>
                    <p className="text-medium text-text-secondary">
                        {appCategories.reduce((n, c) => n + c.apps.length, 0)} apps, {industries.length} industries, {capabilities.length}{" "}
                        capabilities, one platform to run your venture.
                    </p>
                </div>
            </header>
            <div className="relative">
                <div className="group/appsgrid grid grid-cols-5 lg:grid-cols-9 lg:grid-rows-5 gap-px bg-text-tertiary lg:h-screen">
                    {highlightedTiles.map((data, idx) => (
                        <HighlightedTile
                            key={data.name}
                            data={data}
                            position={highlightedPositions[idx] ?? ""}
                            isCenter={idx === 0}
                            idx={idx}
                        />
                    ))}
                    {allTiles.map((data, idx) => (
                        <Tile
                            key={`${data.name}-${idx}`}
                            data={data}
                            idx={idx}
                        />
                    ))}
                </div>
                {/* Four edge fades, each gradient runs from full page-bg at the edge
                 * to transparent toward center. Where two overlap at the corners they
                 * combine to fully obscure the corner cells, producing the soft
                 * "merging with the page" effect. */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-[20vh] bg-gradient-to-b from-surface-primary-background to-transparent"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-[20vh] bg-gradient-to-t from-surface-primary-background to-transparent"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 left-0 w-[15vw] bg-gradient-to-r from-surface-primary-background to-transparent"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 right-0 w-[15vw] bg-gradient-to-l from-surface-primary-background to-transparent"
                />
            </div>
        </section>
    );
}
